// Logs — per-call ERROR history for the CV API (mirror).
//
// Shows individual failed calls, each tagged Upstream (the credit-pull
// provider's fault — exportable to hand to them) or Internal (our side / the
// caller: rate limits, validation, idempotency). Backed by
// GET /api/logs/errors over the mirror handler's structured log lines.
//
// Known gap (surfaced in the UI): unhandled internal 500s currently throw
// before the log line is written, so they are not represented yet.
//
// Also hosts the "Access denials" view (category "denied"): requests blocked
// before reaching the handler (invalid API keys, IP-allowlist rejections),
// backed by GET /api/logs/access-denials.

import { useCallback, useEffect, useMemo, useState } from "react";

import {
  api,
  type AccessDenialEntry,
  type ErrorLogEntry,
  type ErrorLogKind,
} from "../lib/api";
import {
  buttonStyle,
  Card,
  EmptyState,
  PageHeader,
  Pill,
  Skeleton,
  tokens,
} from "../lib/theme";

const WINDOWS: Array<{ label: string; sec: number }> = [
  { label: "Last 1 hour", sec: 3600 },
  { label: "Last 24 hours", sec: 24 * 3600 },
  { label: "Last 7 days", sec: 7 * 24 * 3600 },
  { label: "Last 30 days", sec: 30 * 24 * 3600 },
];

// The category select covers the three error-log kinds plus the access-denials
// view ("denied"), which is backed by a separate endpoint + entry shape.
type LogsKind = ErrorLogKind | "denied";

const KINDS: Array<{ label: string; value: LogsKind }> = [
  { label: "All errors", value: "all" },
  { label: "Upstream only", value: "upstream" },
  { label: "Internal only", value: "internal" },
  { label: "Access denials (blocked)", value: "denied" },
];

function fmtTime(iso: string): string {
  // Logs Insights @timestamp is "YYYY-MM-DD HH:MM:SS.mmm" (UTC). Show as-is
  // (trimmed) — it's already human-readable and unambiguous.
  return iso.replace(/\.\d+$/, "") + " UTC";
}

function csvEscape(v: string | number | null): string {
  if (v === null) return "";
  const s = String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function toCsv(entries: ErrorLogEntry[]): string {
  const header = [
    "timestamp_utc",
    "request_id",
    "api_key",
    "category",
    "error_type",
    "upstream_status",
    "upstream_latency_ms",
    "total_latency_ms",
    "phone_hash",
  ].join(",");
  const rows = entries.map((e) =>
    [
      e.timestamp,
      e.requestId,
      e.clientId,
      e.category,
      e.errorType,
      e.upstreamStatus,
      e.upstreamLatencyMs,
      e.totalLatencyMs,
      e.phoneHash,
    ]
      .map(csvEscape)
      .join(","),
  );
  return [header, ...rows].join("\n");
}

function toDeniedCsv(entries: AccessDenialEntry[]): string {
  const header = [
    "timestamp_utc",
    "source_ip",
    "route",
    "http_status",
    "reason",
  ].join(",");
  const rows = entries.map((e) =>
    [e.timestamp, e.ip, e.route, e.status, e.reason].map(csvEscape).join(","),
  );
  return [header, ...rows].join("\n");
}

export default function Logs() {
  const [kind, setKind] = useState<LogsKind>("all");
  const [windowSec, setWindowSec] = useState<number>(7 * 24 * 3600);
  // Exactly one of these is non-null after a successful load — `entries` for
  // the three error kinds, `denials` for the access-denials view.
  const [entries, setEntries] = useState<ErrorLogEntry[] | null>(null);
  const [denials, setDenials] = useState<AccessDenialEntry[] | null>(null);
  const [limited, setLimited] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshedAt, setRefreshedAt] = useState<Date | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    const req =
      kind === "denied"
        ? api.accessDenials({ windowSec, limit: 1000 }).then((d) => {
            setDenials(d.entries);
            setEntries(null);
            setLimited(d.limited);
          })
        : api.errorLogs({ kind, windowSec, limit: 1000 }).then((d) => {
            setEntries(d.entries);
            setDenials(null);
            setLimited(d.limited);
          });
    req
      .then(() => {
        setError(null);
        setRefreshedAt(new Date());
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : "Failed to load");
      })
      .finally(() => setLoading(false));
  }, [kind, windowSec]);

  useEffect(() => {
    load();
  }, [load]);

  const exportCsv = useCallback(() => {
    let csv: string;
    let filename: string;
    if (kind === "denied") {
      if (!denials || denials.length === 0) return;
      csv = toDeniedCsv(denials);
      filename = `cv-api-access-denials-${Math.round(windowSec / 3600)}h.csv`;
    } else {
      if (!entries || entries.length === 0) return;
      csv = toCsv(entries);
      filename = `cv-api-errors-${kind}-${Math.round(windowSec / 3600)}h.csv`;
    }
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [entries, denials, kind, windowSec]);

  const counts = useMemo(() => {
    const e = entries ?? [];
    return {
      total: e.length,
      upstream: e.filter((x) => x.category === "upstream").length,
      internal: e.filter((x) => x.category === "internal").length,
    };
  }, [entries]);

  // The row set for the selected category — drives the shared loading / error /
  // empty / export states so both modes behave identically.
  const rows = kind === "denied" ? denials : entries;

  const selectStyle: React.CSSProperties = {
    background: tokens.color.bg2,
    color: tokens.color.fg0,
    border: `1px solid ${tokens.color.border}`,
    borderRadius: tokens.radius.md,
    padding: `${tokens.space.sm}px ${tokens.space.md}px`,
    fontSize: tokens.font.size.sm,
    cursor: "pointer",
  };

  return (
    <div>
      <PageHeader
        title="Logs"
        subtitle="Per-call error history · upstream vs internal"
        actions={
          <div style={{ display: "flex", gap: tokens.space.sm, alignItems: "center" }}>
            <select
              value={kind}
              onChange={(e) => setKind(e.target.value as LogsKind)}
              style={selectStyle}
              aria-label="Error category"
            >
              {KINDS.map((k) => (
                <option key={k.value} value={k.value}>
                  {k.label}
                </option>
              ))}
            </select>
            <select
              value={windowSec}
              onChange={(e) => setWindowSec(Number(e.target.value))}
              style={selectStyle}
              aria-label="Time window"
            >
              {WINDOWS.map((w) => (
                <option key={w.sec} value={w.sec}>
                  {w.label}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={exportCsv}
              disabled={!rows || rows.length === 0}
              style={buttonStyle(
                "ghost",
                !rows || rows.length === 0,
              )}
            >
              Export CSV
            </button>
            <button
              type="button"
              onClick={load}
              disabled={loading}
              style={buttonStyle("ghost", loading)}
            >
              {loading ? "Refreshing…" : "Refresh"}
            </button>
          </div>
        }
      />

      {/* Summary line */}
      {rows && (
        <div
          style={{
            display: "flex",
            gap: tokens.space.md,
            alignItems: "center",
            marginBottom: tokens.space.lg,
            color: tokens.color.fg2,
            fontSize: tokens.font.size.sm,
          }}
        >
          {kind === "denied" ? (
            <Pill tone="danger">{rows.length} denied requests</Pill>
          ) : (
            <>
              <span>
                <strong style={{ color: tokens.color.fg0 }}>{counts.total}</strong> errors
              </span>
              <Pill tone="danger">{counts.upstream} upstream</Pill>
              <Pill tone="warning">{counts.internal} internal</Pill>
            </>
          )}
          {refreshedAt && (
            <span style={{ marginLeft: "auto" }}>
              Updated{" "}
              {refreshedAt.toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
                second: "2-digit",
              })}
            </span>
          )}
        </div>
      )}

      {/* Known-gap note (errors) / what-counts note (denials) */}
      <div
        style={{
          fontSize: tokens.font.size.xs,
          color: tokens.color.fg2,
          marginBottom: tokens.space.md,
        }}
      >
        {kind === "denied" ? (
          <>
            Requests blocked before reaching the handler — invalid or missing
            API keys, IP-allowlist rejections, and disallowed routes. Source IP
            is the caller as seen at the edge.
          </>
        ) : (
          <>
            Upstream = the credit-pull provider failed (5xx / auth / timeout) —
            export these to hand to the provider. Internal = our side or the caller
            (rate limit, validation, idempotency). Note: unhandled internal 500s are
            not yet captured per-call.
          </>
        )}
      </div>

      {loading && !rows && (
        <Card>
          <Skeleton height={40} />
          <div style={{ height: 10 }} />
          <Skeleton height={40} />
          <div style={{ height: 10 }} />
          <Skeleton height={40} />
        </Card>
      )}

      {error && !rows && (
        <Card>
          <EmptyState title="Failed to load logs" hint={error} />
        </Card>
      )}

      {rows && rows.length === 0 && !loading && (
        <Card>
          {kind === "denied" ? (
            <EmptyState
              title="No denied requests in this window"
              hint="Nothing was blocked for this time range — requests rejected for invalid API keys or by the IP allowlist will appear here."
            />
          ) : (
            <EmptyState
              title="No errors in this window"
              hint="Nothing failed for the selected category and time range."
            />
          )}
        </Card>
      )}

      {kind !== "denied" && entries && entries.length > 0 && (
        <Card padding={0}>
          {limited && (
            <div
              style={{
                padding: tokens.space.sm,
                fontSize: tokens.font.size.xs,
                color: tokens.color.warning,
                borderBottom: `1px solid ${tokens.color.border}`,
              }}
            >
              Showing the most recent 1,000 — narrow the window to see all.
            </div>
          )}
          <div style={{ overflowX: "auto" }}>
            <table
              style={{
                width: "100%",
                borderCollapse: "separate",
                borderSpacing: 0,
                fontSize: tokens.font.size.sm,
              }}
            >
              <thead>
                <tr>
                  <Th>Time</Th>
                  <Th>Category</Th>
                  <Th>Error</Th>
                  <Th>API key</Th>
                  <Th align="right">Upstream</Th>
                  <Th align="right">Latency</Th>
                  <Th>Request ID</Th>
                </tr>
              </thead>
              <tbody>
                {entries.map((e, idx) => (
                  <tr
                    key={`${e.requestId}-${idx}`}
                    style={{
                      background:
                        idx % 2 === 0 ? "transparent" : "rgba(255,255,255,0.015)",
                    }}
                  >
                    <Td mono>{fmtTime(e.timestamp)}</Td>
                    <Td>
                      <Pill tone={e.category === "upstream" ? "danger" : "warning"}>
                        {e.category}
                      </Pill>
                    </Td>
                    <Td>{e.errorType}</Td>
                    <Td>{e.clientId}</Td>
                    <Td align="right" mono>
                      {e.upstreamStatus ?? "—"}
                    </Td>
                    <Td align="right" mono>
                      {e.totalLatencyMs != null ? `${e.totalLatencyMs} ms` : "—"}
                    </Td>
                    <Td mono>{e.requestId}</Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {kind === "denied" && denials && denials.length > 0 && (
        <Card padding={0}>
          {limited && (
            <div
              style={{
                padding: tokens.space.sm,
                fontSize: tokens.font.size.xs,
                color: tokens.color.warning,
                borderBottom: `1px solid ${tokens.color.border}`,
              }}
            >
              Showing the most recent 1,000 — narrow the window to see all.
            </div>
          )}
          <div style={{ overflowX: "auto" }}>
            <table
              style={{
                width: "100%",
                borderCollapse: "separate",
                borderSpacing: 0,
                fontSize: tokens.font.size.sm,
              }}
            >
              <thead>
                <tr>
                  <Th>Time</Th>
                  <Th>Source IP</Th>
                  <Th>Route</Th>
                  <Th align="right">HTTP</Th>
                  <Th>Reason</Th>
                </tr>
              </thead>
              <tbody>
                {denials.map((e, idx) => (
                  <tr
                    key={`${e.requestId}-${idx}`}
                    style={{
                      background:
                        idx % 2 === 0 ? "transparent" : "rgba(255,255,255,0.015)",
                    }}
                  >
                    <Td mono>{fmtTime(e.timestamp)}</Td>
                    <Td mono>{e.ip}</Td>
                    <Td mono>{e.route}</Td>
                    <Td align="right" mono>
                      {e.status}
                    </Td>
                    <Td>{e.reason}</Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}

function Th({
  children,
  align = "left",
}: {
  children: React.ReactNode;
  align?: "left" | "right";
}) {
  return (
    <th
      style={{
        textAlign: align,
        padding: `${tokens.space.sm}px ${tokens.space.md}px`,
        color: tokens.color.fg2,
        fontWeight: 600,
        fontSize: tokens.font.size.xs,
        textTransform: "uppercase",
        letterSpacing: "0.04em",
        borderBottom: `1px solid ${tokens.color.border}`,
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </th>
  );
}

function Td({
  children,
  align = "left",
  mono = false,
}: {
  children: React.ReactNode;
  align?: "left" | "right";
  mono?: boolean;
}) {
  return (
    <td
      style={{
        textAlign: align,
        padding: `${tokens.space.sm}px ${tokens.space.md}px`,
        color: tokens.color.fg1,
        borderBottom: `1px solid ${tokens.color.border}`,
        whiteSpace: "nowrap",
        fontFamily: mono ? tokens.font.mono : undefined,
        fontSize: mono ? tokens.font.size.xs : undefined,
      }}
    >
      {children}
    </td>
  );
}
